import {FC, useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Button} from '@/components/ui/button/Button'
import {AlertCircleIcon, Send01Icon} from '@/components/ui/icon/Icon'
import {
    SelectedItem,
    UserSelectionItem
} from '@/features/users/components/users-and-groups-multi-select-popover/UsersAndGroupsMultiSelectPopover'
import {CombinedItem} from '@/features/users/hooks/useUsersAndGroupsSearchableList'
import {
    StyledNewConversationForm,
    StyledNewConversationFormInputsContainer,
    StyledNewConversationFormFooter,
    StyledUrgentButton
} from './style'
import {GroupMember} from '@/features/users/types'
import {NewConversationFields} from '../new-conversation-fields/NewConversationFields'
import {ConversationEntityType} from '@/features/conversations/types'
import {z} from 'zod'

// Simplified schema for the form that makes entityType and entity truly optional
const NewConversationFormSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    memberIds: z.array(z.string()).min(1, 'At least one member is required'),
    message: z.string().min(1, 'Message is required'),
    isUrgent: z.boolean().default(false),
    entityType: z.enum(['project', 'treatment_request', 'medical_record_report']).optional(),
    entity: z.any().optional()
})

type NewConversationFormData = z.infer<typeof NewConversationFormSchema>

interface NewConversationFormProps {
    /** Patient scope for the conversation, or null for internal conversations (no patient, no entity selector) */
    patientId: string | null
    onSubmit: (
        data: {
            title: string
            message: string
            memberIds: string[]
            isUrgent: boolean
            entityType?: ConversationEntityType
            entityId?: string
        },
        members: {type: 'user' | 'group'; id: string}[]
    ) => void
    onCancel: () => void
    selectedMembers: GroupMember[]
    onMembersChange: (members: GroupMember[]) => void
    isPendingCreatingNewConversation: boolean
}

export const NewConversationForm: FC<NewConversationFormProps> = ({
    patientId,
    onSubmit,
    selectedMembers,
    onMembersChange,
    isPendingCreatingNewConversation
}) => {
    const {t} = useTranslation()

    // Track selected items like in ConversationParticipants
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])
    const [selectedItemsData, setSelectedItemsData] = useState<Map<string, {name: string; type: 'user' | 'group'}>>(
        new Map()
    )

    const form = useForm<NewConversationFormData>({
        resolver: zodResolver(NewConversationFormSchema),
        defaultValues: {
            title: '',
            memberIds: [],
            message: '',
            isUrgent: false,
            entityType: undefined,
            entity: undefined
        }
    })

    const {
        handleSubmit,
        formState: {isValid, isSubmitting},
        setValue,
        watch,
        reset
    } = form

    const isUrgent = watch('isUrgent')

    // Reset form when component mounts to ensure clean state
    useEffect(() => {
        reset({
            title: '',
            memberIds: [],
            message: '',
            isUrgent: false,
            entityType: undefined,
            entity: undefined
        })
        setSelectedItems([])
        setSelectedItemsData(new Map())
    }, [reset])

    const handleParticipantToggle = (users: UserSelectionItem[], itemData: CombinedItem) => {
        // Determine if we're adding or removing based on the first user's selection status
        const firstUser = users[0]
        if (!firstUser) return

        const isAdding = !selectedItems.some(item => item.type === 'user' && item.id === firstUser.id)

        // Update selected items - always work with users only
        setSelectedItems(prev => {
            if (isAdding) {
                // Add all users from the selection
                const newUserItems = users.map(user => ({
                    type: 'user' as const,
                    id: user.id
                }))

                // Remove any existing items that might conflict
                const filteredPrev = prev.filter(
                    item => !users.some(user => item.type === 'user' && item.id === user.id)
                )

                return [...filteredPrev, ...newUserItems]
            } else {
                // Remove all users from the selection
                return prev.filter(item => !users.some(user => item.type === 'user' && item.id === user.id))
            }
        })

        // Update item data for display
        setSelectedItemsData(prev => {
            const newMap = new Map(prev)

            if (isAdding) {
                // Add data for all users
                users.forEach(user => {
                    const name =
                        itemData.type === 'user'
                            ? itemData.fullName || itemData.name
                            : itemData.members?.find(m => m.id === user.id)?.fullName || `User ${user.id}`
                    newMap.set(user.id, {
                        name: name,
                        type: 'user' as const
                    })
                })
            } else {
                // Remove data for all users
                users.forEach(user => {
                    newMap.delete(user.id)
                })
            }
            return newMap
        })

        // Update form field with all selected user IDs
        const newSelectedItems = isAdding
            ? [...selectedItems, ...users.map(user => ({type: 'user' as const, id: user.id}))]
            : selectedItems.filter(item => !users.some(user => item.type === 'user' && item.id === user.id))

        setValue(
            'memberIds',
            newSelectedItems.map(item => item.id),
            {shouldValidate: true}
        )

        // Update selectedMembers for display compatibility
        if (isAdding) {
            // Add users
            const newMembers = users.map(user => {
                const nameParts = (
                    itemData.type === 'user'
                        ? itemData.fullName || itemData.name
                        : itemData.members?.find(m => m.id === user.id)?.fullName || `User ${user.id}`
                ).split(' ')
                return {
                    id: user.id,
                    firstName: nameParts[0] || 'User',
                    lastName: nameParts.slice(1).join(' ') || '',
                    email: itemData.type === 'user' ? itemData.email || '' : '',
                    avatarUrl: itemData.type === 'user' ? itemData.avatarUrl || null : null
                } as GroupMember
            })
            onMembersChange([...selectedMembers, ...newMembers])
        } else {
            // Remove users
            const newMembers = selectedMembers.filter(member => !users.some(user => member.id === user.id))
            onMembersChange(newMembers)
        }
    }

    const handleFormSubmit = (data: NewConversationFormData) => {
        // Convert selectedItems to the expected member format (only users now)
        const members = selectedItems.map(item => ({
            type: 'user' as const,
            id: item.id
        }))

        // Transform the data to match the expected interface
        const transformedData = {
            title: data.title,
            message: data.message,
            memberIds: data.memberIds,
            isUrgent: data.isUrgent,
            entityType: data.entityType,
            entityId: data.entity?.id
        }

        onSubmit(transformedData, members)
    }

    // Calculate display items for avatars (only users now)
    const currentDisplayItems = Array.from(selectedItemsData.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        type: 'user' as const, // Always 'user' now since we only work with users
        avatarUrl: null
    }))

    return (
        <StyledNewConversationForm onSubmit={handleSubmit(handleFormSubmit)}>
            <StyledNewConversationFormInputsContainer direction="column" fullWidth>
                <FormProvider {...form}>
                    <NewConversationFields
                        selectedItems={selectedItems}
                        handleParticipantToggle={handleParticipantToggle}
                        currentDisplayItems={currentDisplayItems}
                        patientId={patientId}
                    />
                </FormProvider>
                <StyledNewConversationFormFooter justify="space-between" align="center" fullWidth>
                    <StyledUrgentButton
                        type="button"
                        variant="ghost"
                        size="md"
                        $isActive={isUrgent}
                        onClick={() => setValue('isUrgent', !isUrgent)}
                    >
                        <AlertCircleIcon size={18} />
                        {t('conversations:urgent')}
                    </StyledUrgentButton>

                    <Flexbox gap={2}>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={!isValid || isSubmitting || isPendingCreatingNewConversation}
                            size="lg"
                        >
                            {t('conversations:send')}
                            <Send01Icon />
                        </Button>
                    </Flexbox>
                </StyledNewConversationFormFooter>
            </StyledNewConversationFormInputsContainer>
        </StyledNewConversationForm>
    )
}
